import React from 'react'
import styled from 'styled-components'
import { mobile } from '../../utils/media-query'
import { blockTextHeight } from '../../constants'
import heartEyesImg from '../../../images/hearteyes.gif'
import placeholder1 from '../../../images/why_1.gif'
import placeholder2 from '../../../images/why_2.gif'
import placeholder3 from '../../../images/why_3.gif'
import GradientLink from '../../shared/gradient-link'

const blockRowWidth = 320

const ValueGif = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    img {
        width: 100px;
        ${mobile.css`
            width: 60px;
        `}
    }

    h1 {
        margin-bottom: 15px;
    }
`

const Blurb = styled.div`
    text-align: center;
    width: 650px;
    margin-bottom: 75px;

    p {
        margin-bottom: 0;
        font-size: 20px;
        line-height: 28px;
    }

    ${mobile.css`
        width: 100%;
        padding: 0 15px;
        box-sizing: border-box;
        margin-bottom: 40px;
    `}
`

const Blocks = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`

type BlockRowProps = {
    isReverse: boolean
}
const BlockRow = styled.div<BlockRowProps>`
    display: flex;
    flex-direction: ${({ isReverse }) => (isReverse ? 'row-reverse' : 'row')};
    margin-bottom: 30px;

    ${({ isReverse }) =>
        isReverse
            ? mobile.css`
                width: ${blockRowWidth}px;
                flex-direction: column;
                align-self: flex-start;
                margin: 20px 0;
            `
            : mobile.css`
                width: ${blockRowWidth}px;
                flex-direction: column;
                align-self: flex-end;
                align-items: flex-end;
                text-align: right;
                margin: 20px 0;
            `};
`

const BlockImage = styled.div`
    flex-basis: 50%;
    position: relative;
    height: ${blockTextHeight}px;

    img {
        position: absolute;
        top: 0;
        height: 100%;
        width: 672px;

        ${mobile.css`
            height: auto;
            position: unset;
            width: 100%;
        `}
    }

    ${mobile.css`
        height: auto;
    `}
`

type BlockTextProps = {
    isHorizEnd?: boolean
}
const BlockText = styled.div<BlockTextProps>`
    display: flex;
    justify-content: center;
    align-items: ${({ isHorizEnd }) => (isHorizEnd ? 'flex-end' : 'flex-start')};
    flex-direction: column;
    flex-basis: 50%;
    height: ${blockTextHeight}px;

    ${({ isHorizEnd }) => (isHorizEnd ? 'padding-right: 16px' : 'padding-left: 16px')};

    ${mobile.css`
        height: auto;
        padding: 25px 14px 0;
    `}
`

type InfoProps = BlockRowProps
const Info = styled.div<InfoProps>`
    display: flex;
    flex-direction: column;
    width: ${blockRowWidth}px;

    ${({ isReverse }) =>
        isReverse &&
        mobile.css`
            align-items: flex-end;
        `};

    ${mobile.css`
        width: 100%;
    `}
`

const Title = styled.h3`
    margin: 0 0 15px;
    width: 100%;
    line-height: 30px;
`

const SDKValue = () => {
    const rows = [
        {
            title: 'The Most Engaging Content',
            text: `Exclusive content types, world class partners and thousands of unique artists. What more could you ask for? Oh, and we work directly with our partners to license their content — so you can feel secure knowing it’s safe for distribution.`,
            linkText: 'Open in Docs',
            url: '/docs/sdk#content-types',
            src: placeholder1,
            isReverse: true,
        },
        {
            title: `Powerful Search Algorithms`,
            text: `Our unparalleled search algorithm lets users easily express themselves and animate their conversations with the exact GIF they were looking for, or didn’t know they wanted. It also uses analytics to improve results and keep users coming back for more.`,
            linkText: 'Open in Docs',
            url: '/docs/api/endpoint#search',
            src: placeholder2,
            isReverse: false,
        },
        {
            title: 'Optimized Loading & Renditions',
            text: `We offer extensive rendition options for different platforms and devices to ensure loading content doesn’t get in the way of your app’s speed. Better yet, it’s already built directly into our SDK. `,
            linkText: 'Open in Docs',
            url: '/docs/optional-settings/#rendition-guide',
            src: placeholder3,
            isReverse: true,
        },
    ]

    return (
        <section>
            <ValueGif>
                <img src={heartEyesImg} />
                <h1>Why GIPHY?</h1>
            </ValueGif>
            <Blurb>
                <p>
                    As the first and largest GIF search engine that serves more than 10 billion GIFs per day, it’s safe
                    to say GIPHY knows GIFs.
                </p>
            </Blurb>
            <Blocks>
                {rows.map(({ title, text, linkText, url, src, isReverse }, index) => (
                    <BlockRow isReverse={isReverse} key={index}>
                        <BlockImage>
                            <img style={isReverse ? { left: 0 } : { right: 0 }} src={src} />
                        </BlockImage>
                        <BlockText isHorizEnd={!isReverse}>
                            <Info isReverse={!isReverse}>
                                <Title>{title}</Title>
                                <p>{text}</p>
                                <GradientLink text={linkText} url={url} />
                            </Info>
                        </BlockText>
                    </BlockRow>
                ))}
            </Blocks>
        </section>
    )
}

export default SDKValue
